import React, { useState } from 'react';

import {
  openNotificationWithIcon,
  Page,
  PageContent,
  Regex,
  useFetch,
  User,
  PermissionGuard,
} from '@mss/mss-component-library';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import EditSidebar from './Sidebar';
import {
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  useParams,
  useHistory,
} from 'react-router-dom';
import { notification, Modal } from 'antd';
import Permissions from './Permissions';
import emptyUser from '../../../util/emptyUser';
import Settings from './Settings';
import Button from '../../../../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import Api from '../../../services/Api';
import Groups from './Groups';
import { useTranslation } from 'react-i18next';
import ObjectPermissions from './ObjectPermissions';
import {
  PermissionSetOption,
  UserPermissionsAPI,
} from 'jsx/authorization/util/permissionsForEdit';
import { useQueryClient } from '@tanstack/react-query';
import { sanitizedUrl } from '../../../../asset/util/sanitize';

export default function Edit() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [t] = useTranslation(['authorization', 'asset', 'common']);
  const { path, url } = useRouteMatch();
  const { userId = '' } = useParams<{ userId: string }>();
  const validId = Regex.uuid.test(userId);

  const [user, setUser] = useState<User>(emptyUser);
  const [userPermissions, setUserPermissions] = useState<UserPermissionsAPI[]>(
    []
  );
  const [checkedList, setCheckedList] = useState<(string | undefined)[]>([]);

  const [fetchedUserPermissions, fetchUserPermissions] = useFetch({
    url: `/api/user/${userId}/permissions/`,
    defaultValue: [],
    initialFetch: !!userId,
    onSucces: ({ data }) => {
      setUserPermissions(data);
      setCheckedList(
        data.map((userPerm: PermissionSetOption) => userPerm.codename)
      );
    },
    onEmpty: ({ data }) => {
      setUserPermissions(data);
      setCheckedList(
        data.map((userPerm: PermissionSetOption) => userPerm.codename)
      );
    },
  });

  const [fetchedUser, fetchUser] = useFetch({
    url: `/api/user/${userId}/`,
    defaultValue: emptyUser,
    onSucces: ({ data }) => {
      setUser(data);
    },
    onFail: () => {
      notification.error({
        message: t('Failed to get user information'),
        description: !validId
          ? t(
              'It seems there is an invalid user id in the url. Please navigate to the overview page and click on the asset there.'
            )
          : t(
              'We could not retrieve the data for this user. Please try again later.'
            ),
      });
    },
  });

  const deleteUser = (user: User) =>
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: (
        <div>
          <blockquote>
            {user.first_name} {user.last_name}
            <br />
            {user.email}
          </blockquote>
          <p>{t("You're about to delete this user.")}</p>
        </div>
      ),
      onOk: async () => {
        let result = await Api.user.remove(user.id);
        if (result.status === 204) {
          await queryClient.invalidateQueries({
            queryKey: ['users'],
          });

          history.push(`/authorization/users`);
          openNotificationWithIcon(
            notification.success,
            t(`User deleted.`),
            t(`User '(FIRSTNAME) (LASTNAME)' deleted successfully`, {
              firstName: user.first_name,
              lastName: user.last_name,
            })
          );
        } else {
          let response = await result.json();
          openNotificationWithIcon(
            notification.error,
            t(`User deletion failed.`),
            response.error ||
              t(`Reason: (REASON)`, { reason: result.statusText })
          );
          console.error(t('Error!'), result, response.error);
        }
      },
      okButtonProps: { danger: true },
    });

  return (
    <>
      <Page
        breadCrumbs={
          <Breadcrumbs
            label={
              fetchedUser.loading
                ? `${t('Loading')}...`
                : `${user.first_name} ${user.last_name}`
            }
          />
        }
        actions={
          <PermissionGuard codename={'delete_user'}>
            <Button onClick={() => deleteUser(user)} data-ta="deleteUserButton">
              <FontAwesomeIcon icon={faBan} className="mr-1" />
              {t('DELETE USER')}
            </Button>
          </PermissionGuard>
        }
        main={
          <Switch>
            <Route
              path={`${path}/settings`}
              render={() => (
                <PageContent title={t('User settings')}>
                  <Settings user={user} />
                </PageContent>
              )}
            />
            <Route
              path={`${path}/groups`}
              render={() => (
                <PageContent>
                  <Groups user={user} fetchUser={fetchUser} />
                </PageContent>
              )}
            />
            <Route
              path={`${path}/permissions`}
              render={() => (
                <PageContent>
                  <Permissions
                    checkedList={checkedList}
                    setCheckedList={setCheckedList}
                    userPermissions={userPermissions}
                    fetchedUserPermissions={fetchedUserPermissions}
                    fetchUserPermissions={fetchUserPermissions}
                    user={user}
                  />
                </PageContent>
              )}
            />
            <Route
              path={`${path}/objectpermissions`}
              render={() => (
                <ObjectPermissions userPermissions={userPermissions} />
              )}
            />

            <Redirect to={`${sanitizedUrl(url)}/settings`} />
          </Switch>
        }
        sidebar={<EditSidebar />}
      />
    </>
  );
}
